import { Clock, Wifi, Calendar4Range, ShieldCheck, Info } from 'react-bootstrap-icons';
import {
  StyledProductRow,
  StyledMainInfo,
  StyledDetailsRow,
  PointMedian,
  StyledDetailLeft,
  StyledDetailRight,
  StyledPopOverItemContainer
} from './ResultsStyles';
import {
  displayEngagement,
  displayAllEngagements,
  getQualificationItemBillingZone,
  getQualificationItemDelay,
  makeAvailableDateBadge,
  makeUnavailabilityReasonBadge
} from '../../helper';
import { useGuardHook } from '../../../../utils/FeatureFlag';
import { useAppDispatch } from '../../../../app/hooks';
import { addSelectedOptionsToOrder } from '@features/order/reducer/orderSlice';
import Icon from '@designSystem/Icon';
import PopOver from '@designSystem/PopOver';
import Badge from '@designSystem/Badges';
import { IOfferingResult } from '../../interfaces';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { billingZoneMap, listOfLabelsShort } from '../constants';
import { useTranslation } from 'react-i18next';
import ProductPriceAndFees from '../productDetail/ProductPriceAndFees';
import theme from '@theme';

type ResultsItemExpandedProps = {
  showPopOver: string | false;
  setShowPopOver: CallableFunction;
  defaultProduct: IOfferingResult;
  selectedProduct: IOfferingResult;
  similarProducts?: IOfferingResult[];
  displayProductName: string;
  onProductChange?: (product: IOfferingResult) => void;
};

const ResultsItemExpanded = ({
  defaultProduct,
  selectedProduct,
  similarProducts = [],
  onProductChange,
  displayProductName,
  setShowPopOver,
  showPopOver
}: ResultsItemExpandedProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canDisplayPrice = useGuardHook('display_product_price');

  const allProducts = [...similarProducts];

  const currentProduct = selectedProduct
    ? allProducts.find(
        (product) =>
          product.product.productOffering.id === selectedProduct.product.productOffering.id
      ) || defaultProduct
    : defaultProduct;

  const [currentEngagement, setCurrentEngagement] = useState<string>('');

  const billingZone = getQualificationItemBillingZone(currentProduct);
  const needStudy = currentProduct.qualificationItemResult?.toUpperCase() === 'TO_BE_STUDIED';
  const shouldDisplayPriceAndFees = Boolean(currentProduct.product.productPrice?.length);
  const deliveryDelay = getQualificationItemDelay(currentProduct);

  const extractSpeed = (description?: string) => {
    const speedMatch = description?.match(/(\d+)\s*(?:Gbps|Mbps|Mbit\/s)/i);
    return speedMatch ? speedMatch[0] : '';
  };

  const speeds = useMemo(() => {
    const allSpeeds = allProducts
      .map((product) => extractSpeed(product.product.productOffering.description))
      .filter(Boolean);
    return Array.from(new Set(allSpeeds));
  }, [allProducts]);

  const currentSpeed = useMemo(
    () => extractSpeed(currentProduct.product.productOffering.description) || '',
    [currentProduct]
  );

  const getAvailableEngagements = useCallback(
    (speed: string) => {
      const productsWithSpeed = allProducts.filter(
        (product) => extractSpeed(product.product.productOffering.description) === speed
      );

      const allEngagements = productsWithSpeed.flatMap(
        (product) => displayAllEngagements(product.product.productPrice, listOfLabelsShort) || []
      );

      return Array.from(new Set(allEngagements.filter(Boolean)));
    },
    [allProducts]
  );

  const availableEngagements = useMemo(
    () => getAvailableEngagements(currentSpeed),
    [currentSpeed, getAvailableEngagements]
  );

  const gtrMatch = currentProduct.product.productOffering.description?.match(/GTR\s*(\d+)h/i);
  const gtr = gtrMatch ? gtrMatch[1] : '';

  useEffect(() => {
    handleEngagementChange(
      displayEngagement(currentProduct.product.productPrice, listOfLabelsShort) ?? ''
    );
  }, [currentProduct]);

  const handleProductChange = (newProduct: IOfferingResult) => {
    onProductChange?.(newProduct);
  };

  const handleSpeedChange = (speed: string) => {
    const engagementsForSpeed = getAvailableEngagements(speed);
    const matchingProduct = allProducts.find((product) => {
      const productSpeed = extractSpeed(product.product.productOffering.description);
      const engagement = displayEngagement(product.product.productPrice, listOfLabelsShort);
      return productSpeed === speed && engagement === engagementsForSpeed[0];
    });

    if (matchingProduct) {
      handleProductChange(matchingProduct);
      handleEngagementChange(
        displayEngagement(matchingProduct.product.productPrice, listOfLabelsShort) || ''
      );
    }
  };

  const handleEngagementChange = (engagement: string) => {
    setCurrentEngagement(engagement);
    const durationMatch = engagement.match(/(\d+)/);
    const duration = durationMatch ? durationMatch[1] : '36';

    if (duration) {
      dispatch(addSelectedOptionsToOrder({ duration }));
    }
  };

  const togglePopOver = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const newPopOverValue =
      showPopOver === currentProduct.product.productOffering.id
        ? false
        : currentProduct.product.productOffering.id;
    setShowPopOver(newPopOverValue);
  };

  const renderPopOver = () => {
    if (showPopOver && currentProduct.product.productOffering.id === showPopOver) {
      return (
        <PopOver vertical="bottom" horizontal="right">
          {currentProduct.product?.productOffering?.description?.split('\n').map((item: string) => (
            <StyledPopOverItemContainer key={item}>{item}</StyledPopOverItemContainer>
          ))}
        </PopOver>
      );
    }
    return null;
  };

  return (
    <StyledProductRow>
      <StyledMainInfo>
        <div className="product-badges">
          <Badge
            variant="grey"
            design="big"
            style={{
              paddingTop: '4px',
              paddingBottom: '4px',
              color: theme.palette.grey[700],
              border: `1px solid ${theme.palette.grey[200]}`
            }}
          >
            {displayProductName}
          </Badge>
          <Icon
            background
            round
            variant="blue"
            size="minimal"
            variantDeclination={500}
            className="clickable"
            onClick={togglePopOver}
            style={{ position: 'relative' }}
          >
            <Info />
            {renderPopOver()}
          </Icon>
          <span className="separator">/</span>
          {makeUnavailabilityReasonBadge(currentProduct)}
          {makeAvailableDateBadge(currentProduct)}
          {billingZone && (
            <Badge
              variant={billingZoneMap[billingZone]?.badgeVariant || 'blue'}
              style={{ marginLeft: '6px' }}
            >
              {billingZoneMap[billingZone]?.label || ''}
            </Badge>
          )}
        </div>
        {canDisplayPrice && (shouldDisplayPriceAndFees || needStudy) && (
          <ProductPriceAndFees
            style={{ textAlign: 'right' }}
            key={currentProduct.product.productOffering.id}
            product={currentProduct}
            allowEngagement={false}
          />
        )}
      </StyledMainInfo>

      <StyledDetailsRow>
        <div>
          <StyledDetailLeft>
            <Wifi />
            <span className="label">{t('features.order.orderInformations.speed', 'Débit')}</span>
            <select
              value={currentSpeed}
              onChange={(e) => handleSpeedChange(e.target.value)}
              disabled={speeds.length <= 1}
            >
              {speeds.map((speed) => (
                <option key={speed} value={speed}>
                  {speed.replace(/Gbps/i, 'Gbit/s').replace(/Mbps/i, 'Mbit/s')}
                </option>
              ))}
            </select>
          </StyledDetailLeft>

          {availableEngagements.length > 0 && (
            <>
              <PointMedian />
              <StyledDetailLeft>
                <Calendar4Range />
                <span className="label">
                  {t('features.order.orderInformations.engagement', 'Engagement')}
                </span>
                <select
                  value={currentEngagement}
                  onChange={(e) => handleEngagementChange(e.target.value)}
                  disabled={availableEngagements.length <= 1}
                >
                  {availableEngagements.map((eng) => (
                    <option key={eng} value={eng || ''}>
                      {eng}
                    </option>
                  ))}
                </select>
              </StyledDetailLeft>
            </>
          )}
        </div>

        <div style={{ justifyContent: 'end' }}>
          {deliveryDelay && (
            <StyledDetailRight color="blue">
              <Clock />
              <span className="label">Délais de livraison</span>
              <span className="value">{deliveryDelay}</span>
            </StyledDetailRight>
          )}
          {gtr && (
            <StyledDetailRight>
              <ShieldCheck />
              <span className="label">GTR</span>
              <span className="value">{gtr} Heures, jours ouvrés</span>
            </StyledDetailRight>
          )}
        </div>
      </StyledDetailsRow>
    </StyledProductRow>
  );
};

export default ResultsItemExpanded;
